import { useState, useEffect, useCallback } from 'react';
import publicApiClient from '../utils/publicApiClient';
import {
    readStoredGuestCredentials,
    writeStoredGuestCredentials,
    clearStoredGuestCredentials,
    GUEST_SESSION_ERROR_CODES,
} from '../utils/guestSessionStorage';

const isTableOccupiedError = (err) =>
    err?.status === 409 && err?.reason === GUEST_SESSION_ERROR_CODES.TABLE_OCCUPIED;

export const useGuestSession = (restaurantId, tableNumber) => {
    const [sessionId, setSessionId] = useState(null);
    const [joinCode, setJoinCode] = useState(null);
    const [sessionState, setSessionState] = useState('browsing');
    const [linkedOrder, setLinkedOrder] = useState(null);
    const [sessionError, setSessionError] = useState(null);

    const isDineIn = Boolean(tableNumber);
    const hasSession = sessionState === 'authenticated';

    const applySession = useCallback((data, code) => {
        if (!data?.sessionId || !code) {
            return null;
        }

        publicApiClient.setGuestSession(data.sessionId, code);
        setSessionId(data.sessionId);
        setJoinCode(code);
        setLinkedOrder(data.order || null);

        if (restaurantId) {
            writeStoredGuestCredentials(restaurantId, tableNumber, {
                sessionId: data.sessionId,
                joinCode: code,
            });
        }

        setSessionState('authenticated');
        setSessionError(null);
        return data.sessionId;
    }, [restaurantId, tableNumber]);

    const applyOrderResponse = useCallback((response) => {
        const guestSession = response?.data?.guestSession;

        if (!guestSession?.sessionId || !guestSession?.joinCode) {
            return null;
        }

        return applySession(
            { sessionId: guestSession.sessionId, order: response.data?.order || null },
            guestSession.joinCode
        );
    }, [applySession]);

    const setNeedsJoin = useCallback(() => {
        setSessionState('needs_join');
        setSessionError(null);
    }, []);

    const joinSession = useCallback(async (submittedCode) => {
        const normalizedCode = String(submittedCode || '').trim().toUpperCase();

        const response = await publicApiClient.post('/public/sessions/join', {
            restaurant: restaurantId,
            tableNumber,
            joinCode: normalizedCode,
        });

        setSessionError(null);
        return applySession(response.data, normalizedCode);
    }, [restaurantId, tableNumber, applySession]);

    const reloadSession = useCallback(async () => {
        const stored = readStoredGuestCredentials(restaurantId, tableNumber);
        const activeSessionId = sessionId || stored?.sessionId;
        const activeJoinCode = joinCode || stored?.joinCode;

        if (!activeSessionId || !activeJoinCode) {
            return null;
        }

        publicApiClient.setGuestSession(activeSessionId, activeJoinCode);

        const response = await publicApiClient.get('/public/sessions/current');
        applySession(response.data, activeJoinCode);
        return response.data;
    }, [sessionId, joinCode, restaurantId, tableNumber, applySession]);

    const refreshSession = useCallback(() => {
        clearStoredGuestCredentials(restaurantId, tableNumber);
        publicApiClient.clearGuestSession();
        setSessionId(null);
        setJoinCode(null);
        setLinkedOrder(null);
        setSessionError(null);
        setSessionState('browsing');
        return null;
    }, [restaurantId, tableNumber]);

    useEffect(() => {
        let cancelled = false;

        if (!restaurantId) {
            setSessionError('Restaurant ID is required');
            setSessionState('error');
            return undefined;
        }

        setSessionState('browsing');
        setSessionError(null);

        const stored = readStoredGuestCredentials(restaurantId, tableNumber);

        if (!stored) {
            publicApiClient.clearGuestSession();
            return undefined;
        }

        publicApiClient.setGuestSession(stored.sessionId, stored.joinCode);

        publicApiClient.get('/public/sessions/current')
            .then((response) => {
                if (!cancelled) {
                    applySession(response.data, stored.joinCode);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    clearStoredGuestCredentials(restaurantId, tableNumber);
                    publicApiClient.clearGuestSession();
                    setSessionId(null);
                    setJoinCode(null);
                    setLinkedOrder(null);
                    setSessionState('browsing');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [restaurantId, tableNumber, applySession]);

    return {
        sessionId,
        joinCode,
        sessionState,
        sessionReady: true,
        hasSession,
        linkedOrder,
        sessionError,
        refreshSession,
        reloadSession,
        joinSession,
        applyOrderResponse,
        setNeedsJoin,
        isTableOccupiedError,
        isDineIn,
    };
};

export default useGuestSession;
